// Add User / Edit User modal — replaces the Bootstrap modal in
// web/admin.html. Same input fields + same backend contract; under
// the hood it uses the admin API client + native dialog elements.
//
// Org-membership editing in edit mode mirrors saveUserModal()'s
// diff-then-apply pattern: PUT the metadata first, then add/remove
// individual org memberships, then PUT the default-org change.

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  AuthorizedUserRow,
  CreateUserRequest,
  UpdateUserRequest,
} from "../../api/admin";
import { adminApi } from "../../api/admin";

const ROLES_ALL = ["Super Admin", "Admin", "User"];
const ROLES_ORG_ADMIN = ["Admin", "User"];

interface Props {
  bearer: string;
  mode: "create" | "edit";
  target: AuthorizedUserRow | null;
  orgs: string[];
  isSuperAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function UserModal({
  bearer,
  mode,
  target,
  orgs,
  isSuperAdmin,
  onClose,
  onSaved,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (!dlg.open) dlg.showModal();
    // Close on Escape — `dialog` handles this for us, but the cancel
    // event lets us hook our onClose so the parent can clear state.
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dlg.addEventListener("cancel", onCancel);
    return () => {
      dlg.removeEventListener("cancel", onCancel);
      try {
        if (dlg.open) dlg.close();
      } catch {
        /* ignore */
      }
    };
  }, [onClose]);

  // Seed form state from the target (edit) or empty defaults (create).
  const seedOrgs = useMemo(
    () => (target?.organizations || []).map((o) => o.name),
    [target],
  );
  const seedDefault =
    target?.organizations?.find((o) => o.is_default)?.name ?? seedOrgs[0] ?? "";

  const [email, setEmail] = useState(target?.email ?? "");
  const [name, setName] = useState(target?.name ?? "");
  const [role, setRole] = useState(target?.role ?? "User");
  const [orgRole, setOrgRole] = useState(target?.org_role ?? "");
  const [dept, setDept] = useState(target?.org_dept_name ?? "");
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>(seedOrgs);
  const [defaultOrg, setDefaultOrg] = useState<string>(seedDefault);
  const [createOrg, setCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const roleOptions = isSuperAdmin ? ROLES_ALL : ROLES_ORG_ADMIN;

  // Keep the default-org select consistent with the org multi-select.
  useEffect(() => {
    if (!selectedOrgs.includes(defaultOrg)) {
      setDefaultOrg(selectedOrgs[0] ?? "");
    }
  }, [selectedOrgs, defaultOrg]);

  const toggleOrg = (org: string) => {
    setSelectedOrgs((cur) =>
      cur.includes(org) ? cur.filter((o) => o !== org) : [...cur, org],
    );
  };

  const handleSave = async () => {
    setError(null);

    if (mode === "create" && !email.trim()) {
      setError("Email is required.");
      return;
    }
    if (createOrg && !newOrgName.trim()) {
      setError("Organization name is required.");
      return;
    }
    if (mode === "create" && !createOrg && selectedOrgs.length === 0) {
      setError("Select at least one organization.");
      return;
    }

    setSaving(true);
    try {
      if (mode === "create") {
        const body: CreateUserRequest = {
          email: email.trim(),
          name: name.trim() || undefined,
          role,
          organizations: createOrg ? [newOrgName.trim()] : selectedOrgs,
          default_org: createOrg ? newOrgName.trim() : defaultOrg,
          org_role: orgRole.trim() || undefined,
          org_dept_name: dept.trim() || undefined,
          create_org: createOrg,
        };
        await adminApi.createUser(bearer, body);
      } else if (target) {
        const body: UpdateUserRequest = {
          name: name.trim() || undefined,
          role,
          org_role: orgRole.trim() || undefined,
          org_dept_name: dept.trim() || undefined,
        };
        await adminApi.updateUser(bearer, target.email, body);
        if (isSuperAdmin) {
          const originalOrgs = seedOrgs;
          const added = selectedOrgs.filter((o) => !originalOrgs.includes(o));
          const removed = originalOrgs.filter((o) => !selectedOrgs.includes(o));
          for (const o of added) {
            await adminApi.addUserOrg(bearer, target.email, {
              organization: o,
              is_default: o === defaultOrg,
            });
          }
          for (const o of removed) {
            await adminApi.removeUserOrg(bearer, target.email, o);
          }
          if (
            defaultOrg &&
            defaultOrg !== seedDefault &&
            selectedOrgs.includes(defaultOrg)
          ) {
            await adminApi.setUserDefaultOrg(bearer, target.email, defaultOrg);
          }
        }
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-[14px] border border-line shadow-card p-0 w-[520px] max-w-full backdrop:bg-black/30"
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        className="p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            {mode === "create" ? "Add User" : "Edit User"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={mode === "edit"}
            className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-neutral-50 disabled:text-neutral-500"
          />
        </Field>

        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </Field>

        <Field label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>

        {mode === "create" && isSuperAdmin && (
          <Field label="" labelHidden>
            <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={createOrg}
                onChange={(e) => setCreateOrg(e.target.checked)}
                className="rounded border-line text-brand focus:ring-brand"
              />
              <span>Create a new organization for this user</span>
            </label>
          </Field>
        )}

        {createOrg ? (
          <Field label="New Organization name">
            <input
              type="text"
              required
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </Field>
        ) : (
          isSuperAdmin && (
            <>
              <Field label="Organizations">
                <div className="max-h-32 overflow-y-auto rounded-md border border-line p-2 space-y-1 bg-white">
                  {orgs.length === 0 && (
                    <div className="text-xs text-neutral-500">No organizations available.</div>
                  )}
                  {orgs.map((o) => (
                    <label
                      key={o}
                      className="flex items-center gap-2 text-sm text-neutral-700"
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrgs.includes(o)}
                        onChange={() => toggleOrg(o)}
                        className="rounded border-line text-brand focus:ring-brand"
                      />
                      <span>{o}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Default Organization">
                <select
                  value={defaultOrg}
                  onChange={(e) => setDefaultOrg(e.target.value)}
                  disabled={selectedOrgs.length === 0}
                  className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-neutral-50"
                >
                  {selectedOrgs.length === 0 && <option value="">—</option>}
                  {selectedOrgs.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )
        )}

        <Field label="Org Role">
          <input
            type="text"
            value={orgRole}
            onChange={(e) => setOrgRole(e.target.value)}
            className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </Field>

        <Field label="Department">
          <input
            type="text"
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            className="block w-full rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </Field>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-line">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-line px-3 h-9 text-sm hover:bg-chip"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-brand text-white px-3 h-9 text-sm font-medium hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function Field({
  label,
  children,
  labelHidden,
}: {
  label: string;
  children: React.ReactNode;
  labelHidden?: boolean;
}) {
  return (
    <div>
      {!labelHidden && (
        <label className="block text-xs text-neutral-500 mb-1">{label}</label>
      )}
      {children}
    </div>
  );
}
